import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { downloadImportTemplate } from '../../lib/importTemplates.js';

interface Props {
  onClose: () => void;
  entity: string;
}

type Step = 'upload' | 'map' | 'resolve' | 'review';

interface AgencySuggestion { code: string; name: string; score: number }
interface UnmatchedAgency { value: string; count: number; suggestions: AgencySuggestion[]; proposedCode: string }
type AgencyDecision =
  | { action: 'link'; code: string }
  | { action: 'create'; code: string }
  | { action: 'skip' };

interface EnumOption { code: string; label: string }
interface UnmatchedEnumValue { value: string; count: number; suggestions: { code: string; label: string; score: number }[]; options: EnumOption[] }
type EnumDecision = { action: 'link'; code: string } | { action: 'fallback'; code: string };

interface ImpField { l: string; req: boolean }

const ENTITY_LABEL: Record<string, string> = {
  workers: 'Workers',
  projects: 'Projects',
  customers: 'Customers',
  contacts: 'Contacts',
  suppliers: 'Suppliers',
  training: 'Training',
  deploylog: 'Deployment Log',
};

const IMP_FIELDS: Record<string, ImpField[]> = {
  workers: [
    { l: 'Full Name', req: true },
    { l: 'Primary Role', req: false },
    { l: 'Phone', req: false },
    { l: 'Email', req: false },
    { l: 'Postcode', req: false },
    { l: 'Address', req: false },
    { l: 'Status', req: false },
    { l: 'Emergency Contact', req: false },
    { l: 'Company Ref', req: false },
    { l: 'Payroll Ref', req: false },
    { l: 'Agency', req: false },
  ],
  projects: [
    { l: 'Project ID', req: true },
    { l: 'Name', req: true },
    { l: 'Customer', req: false },
    { l: 'Customer Ref', req: false },
    { l: 'Value', req: false },
    { l: 'Status', req: false },
    { l: 'Manager', req: false },
    { l: 'Manager Phone', req: false },
    { l: 'Start Date', req: false },
    { l: 'End Date', req: false },
    { l: 'Address', req: false },
    { l: 'Postcode', req: false },
  ],
  customers: [
    { l: 'Company Name', req: true },
    { l: 'Type', req: false },
    { l: 'Sector', req: false },
    { l: 'Address', req: false },
    { l: 'Phone', req: false },
    { l: 'Email', req: false },
  ],
  contacts: [
    { l: 'Full Name', req: true },
    { l: 'Company', req: false },
    { l: 'Job Title', req: false },
    { l: 'Department', req: false },
    { l: 'Email', req: false },
    { l: 'Phone', req: false },
  ],
  suppliers: [
    { l: 'Company Name', req: true },
    { l: 'Category', req: false },
    { l: 'Compliance', req: false },
    { l: 'Address', req: false },
    { l: 'Phone', req: false },
    { l: 'Email', req: false },
  ],
  training: [
    { l: 'Worker Name', req: true },
    { l: 'Cert Type', req: true },
    { l: 'Expiry Date', req: false },
    { l: 'Status', req: false },
    { l: 'Doc Reference', req: false },
  ],
  deploylog: [
    { l: 'Worker Name', req: true },
    { l: 'Project ID', req: true },
    { l: 'Start Date', req: false },
    { l: 'Start Time', req: false },
    { l: 'Duration', req: false },
    { l: 'Supervisor', req: false },
    { l: 'Status', req: false },
    { l: 'Notes', req: false },
  ],
};

export function UploadModal({ onClose, entity }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ headers: string[]; rows: Record<string, string>[] } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [unmatched, setUnmatched] = useState<UnmatchedAgency[]>([]);
  const [decisions, setDecisions] = useState<Record<string, AgencyDecision>>({});
  const [unmatchedTypes, setUnmatchedTypes] = useState<UnmatchedEnumValue[]>([]);
  const [typeDecisions, setTypeDecisions] = useState<Record<string, EnumDecision>>({});
  const [result, setResult] = useState<{ created?: number; updated?: number; skipped?: number; errors?: string[]; warnings?: string[] } | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const parse = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('entity', entity);
      return api.post<{ headers: string[]; rows: Record<string, string>[] }>('/imports/parse', fd).then((r) => r.data);
    },
    onSuccess: async (data) => {
      setPreview(data);
      try {
        const suggested = await api
          .post<Record<string, string>>('/imports/suggest-mapping', { entity, headers: data.headers })
          .then((r) => r.data);
        const inverted: Record<string, string> = {};
        for (const [clsField, csvHeader] of Object.entries(suggested)) {
          if (csvHeader) inverted[csvHeader] = clsField;
        }
        setMapping(inverted);
      } catch {
        // non-fatal — user can map manually
      }
      setStep('map');
    },
    onError: (e: any) => toast.error('Parse failed', e?.response?.data?.message ?? 'Check the file and try again'),
  });

  const buildColumnMap = (): Record<string, string> => {
    if (!preview) return {};
    const m: Record<string, string> = {};
    preview.headers.forEach((h) => { m[h] = mapping[h] ?? h; });
    return m;
  };

  const preflight = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('No preview');
      return api.post<{ unmatchedAgencies: UnmatchedAgency[]; unmatchedCustomerTypes?: UnmatchedEnumValue[] }>('/imports/preflight', {
        entity, columnMap: buildColumnMap(), rows: preview.rows,
      }).then((r) => r.data);
    },
    onSuccess: (data) => {
      const types = data.unmatchedCustomerTypes ?? [];
      if (data.unmatchedAgencies.length === 0 && types.length === 0) {
        // Nothing to disambiguate — go straight to import.
        run.mutate();
        return;
      }
      const init: Record<string, AgencyDecision> = {};
      for (const u of data.unmatchedAgencies) {
        if (u.suggestions[0] && u.suggestions[0].score >= 0.85) {
          init[u.value] = { action: 'link', code: u.suggestions[0].code };
        } else {
          init[u.value] = { action: 'create', code: u.proposedCode };
        }
      }
      const initTypes: Record<string, EnumDecision> = {};
      for (const u of types) {
        if (u.suggestions[0] && u.suggestions[0].score >= 0.7) {
          initTypes[u.value] = { action: 'link', code: u.suggestions[0].code };
        } else {
          initTypes[u.value] = { action: 'fallback', code: 'Other' };
        }
      }
      setUnmatched(data.unmatchedAgencies);
      setDecisions(init);
      setUnmatchedTypes(types);
      setTypeDecisions(initTypes);
      setStep('resolve');
    },
    onError: () => toast.error('Preflight failed'),
  });

  const run = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('No preview');
      const body: Record<string, unknown> = { entity, mode: 'skip', columnMap: buildColumnMap(), rows: preview.rows };
      if (Object.keys(decisions).length > 0) body.agencyDecisions = decisions;
      if (Object.keys(typeDecisions).length > 0) body.customerTypeDecisions = typeDecisions;
      return api.post<typeof result>('/imports/run', body).then((r) => r.data);
    },
    onSuccess: (data) => {
      setResult(data);
      setStep('review');
      qc.invalidateQueries();
      toast.success('Import complete');
    },
    onError: () => toast.error('Import failed'),
  });

  const onFile = (f: File) => { setFile(f); parse.mutate(f); };

  // Display step indicator: 4 segments. Resolve only highlighted when active.
  const stepOrder: Step[] = ['upload', 'map', 'resolve', 'review'];
  const stepLabels: Record<Step, string> = { upload: 'UPLOAD', map: 'MAP COLUMNS', resolve: 'RESOLVE', review: 'REVIEW' };
  const currentIdx = stepOrder.indexOf(step);

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 700, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div className="modal-title" id="upload-mo-title">⬆ Import {ENTITY_LABEL[entity] ?? entity}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }} id="upload-mo-sub">Upload a CSV or Excel file — first row must be column headers</div>
          </div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 0, marginBottom: 18, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
          {stepOrder.map((s, i) => {
            const active = s === step;
            const reached = i <= currentIdx;
            return (
              <div key={s} style={{ flex: 1, padding: 8, textAlign: 'center', fontSize: 10, fontWeight: 700, background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#1E2328' : reached ? 'var(--ink)' : 'var(--muted)', letterSpacing: 1 }}>
                {i + 1} · {stepLabels[s]}
              </div>
            );
          })}
        </div>
        {step === 'upload' ? (
          <div id="imp-step-1">
            <div
              className="drop-zone"
              style={{ padding: 30, borderRadius: 10, border: '2px dashed var(--border)', textAlign: 'center', cursor: 'pointer', marginBottom: 12, background: 'var(--card)' }}
              onClick={() => document.getElementById('upload-file-input')?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
            >
              <div style={{ fontSize: 32, marginBottom: 6 }}>📂</div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{file ? `📎 ${file.name}` : 'Drop CSV or Excel file here'}</div>
              <div style={{ fontSize: 11, color: 'var(--faint)' }}>or click to browse — .csv, .xlsx, .xls supported</div>
              <input type="file" id="upload-file-input" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            </div>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 11, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div className="sec-title" style={{ margin: 0 }}>Expected Columns</div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    if (downloadImportTemplate(entity)) toast.success('Template downloaded', `CLS_${entity}_template.csv`);
                    else toast.warn('No template', `No CSV template defined for "${entity}"`);
                  }}
                >
                  ⬇ Download Template
                </button>
              </div>
              <div id="upload-format-hint" style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.9 }}>
                {(IMP_FIELDS[entity] ?? []).map((f) => (
                  <span
                    key={f.l}
                    style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 8px', fontSize: 10, margin: 2, display: 'inline-block' }}
                  >
                    {f.l}{f.req ? <span style={{ color: 'var(--accent)' }}> *</span> : null}
                  </span>
                ))}
                {(IMP_FIELDS[entity] ?? []).some((f) => f.req) ? (
                  <div style={{ marginTop: 6, fontSize: 10, color: 'var(--faint)' }}>* Required</div>
                ) : null}
              </div>
            </div>
            {parse.isPending ? <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>Parsing…</div> : null}
          </div>
        ) : null}
        {step === 'map' && preview ? (
          <div id="imp-step-2">
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>Map your file's columns to CLS fields. Auto-matched where possible.</div>
            <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              <table style={{ width: '100%', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>Source column</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Sample</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>CLS field</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map((h) => (
                    <tr key={h}>
                      <td style={{ padding: 8, fontWeight: 600 }}>{h}</td>
                      <td style={{ padding: 8, color: 'var(--muted)' }}>{preview.rows[0]?.[h] ?? ''}</td>
                      <td style={{ padding: 8 }}>
                        <input type="text" className="form-input" value={mapping[h] ?? h} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} style={{ fontSize: 11 }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setStep('upload')}>← Back</button>
              <button className="btn btn-primary" onClick={() => preflight.mutate()} disabled={preflight.isPending || run.isPending}>
                {preflight.isPending ? 'Checking…' : run.isPending ? 'Importing…' : 'Continue →'}
              </button>
            </div>
          </div>
        ) : null}
        {step === 'resolve' && (unmatched.length > 0 || unmatchedTypes.length > 0) ? (
          <div id="imp-step-resolve">
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
              Some values in your file don't exactly match what's in the system. Pick how to resolve each one. Defaults are auto-suggested when there's a strong match.
            </div>
            {unmatched.length > 0 ? (
            <>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: 'var(--accent)' }}>Agencies</div>
            <div style={{ maxHeight: 250, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
              <table style={{ width: '100%', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>From CSV</th>
                    <th style={{ textAlign: 'right', padding: 8, width: 60 }}>Workers</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatched.map((u) => {
                    const dec = decisions[u.value];
                    const selectedKey =
                      dec?.action === 'link' ? `link:${dec.code}`
                      : dec?.action === 'skip' ? 'skip'
                      : 'create';
                    return (
                      <tr key={u.value} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: 8, fontWeight: 600 }}>{u.value}</td>
                        <td style={{ padding: 8, textAlign: 'right', color: 'var(--muted)' }}>{u.count}</td>
                        <td style={{ padding: 8 }}>
                          <select
                            className="form-input"
                            style={{ fontSize: 11, marginBottom: 4, width: '100%' }}
                            value={selectedKey}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'create') {
                                setDecisions({ ...decisions, [u.value]: { action: 'create', code: u.proposedCode } });
                              } else if (v.startsWith('link:')) {
                                setDecisions({ ...decisions, [u.value]: { action: 'link', code: v.slice(5) } });
                              } else if (v === 'skip') {
                                setDecisions({ ...decisions, [u.value]: { action: 'skip' } });
                              }
                            }}
                          >
                            {u.suggestions.map((s) => (
                              <option key={s.code} value={`link:${s.code}`}>
                                Link to {s.code} — {s.name} ({Math.round(s.score * 100)}% match)
                              </option>
                            ))}
                            <option value="create">Create new agency</option>
                            <option value="skip">Leave blank for these workers</option>
                          </select>
                          {dec?.action === 'create' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                              <span style={{ color: 'var(--muted)' }}>Code:</span>
                              <input
                                type="text"
                                className="form-input"
                                style={{ fontSize: 11, width: 160 }}
                                value={dec.code}
                                onChange={(e) => setDecisions({ ...decisions, [u.value]: { action: 'create', code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') } })}
                              />
                              <span style={{ color: 'var(--muted)' }}>name "{u.value}"</span>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
            ) : null}
            {unmatchedTypes.length > 0 ? (
            <>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: 'var(--accent)' }}>Customer Types</div>
            <div style={{ maxHeight: 250, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
              <table style={{ width: '100%', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>From CSV</th>
                    <th style={{ textAlign: 'right', padding: 8, width: 60 }}>Customers</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Use as</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatchedTypes.map((u) => {
                    const dec = typeDecisions[u.value];
                    const selectedKey = dec?.action === 'fallback' ? `fallback:${dec.code}` : dec?.code ? `link:${dec.code}` : `fallback:Other`;
                    const suggestionCodes = new Set(u.suggestions.map((s) => s.code));
                    const otherOptions = u.options.filter((o) => !suggestionCodes.has(o.code));
                    return (
                      <tr key={u.value} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: 8, fontWeight: 600 }}>{u.value}</td>
                        <td style={{ padding: 8, textAlign: 'right', color: 'var(--muted)' }}>{u.count}</td>
                        <td style={{ padding: 8 }}>
                          <select
                            className="form-input"
                            style={{ fontSize: 11, width: '100%' }}
                            value={selectedKey}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v.startsWith('link:')) {
                                setTypeDecisions({ ...typeDecisions, [u.value]: { action: 'link', code: v.slice(5) } });
                              } else if (v.startsWith('fallback:')) {
                                setTypeDecisions({ ...typeDecisions, [u.value]: { action: 'fallback', code: v.slice(9) } });
                              }
                            }}
                          >
                            {u.suggestions.length > 0 ? (
                              <optgroup label="Suggested matches">
                                {u.suggestions.map((s) => (
                                  <option key={s.code} value={`link:${s.code}`}>
                                    Use {s.label} ({Math.round(s.score * 100)}% match)
                                  </option>
                                ))}
                              </optgroup>
                            ) : null}
                            <optgroup label="All customer types">
                              {otherOptions.map((o) => (
                                <option key={o.code} value={`link:${o.code}`}>
                                  Use {o.label}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Fallback">
                              <option value={`fallback:Other`}>Map all to "Other"</option>
                            </optgroup>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
            ) : null}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setStep('map')}>← Back</button>
              <button
                className="btn btn-primary"
                disabled={run.isPending || unmatched.some((u) => {
                  const d = decisions[u.value];
                  if (!d) return true;
                  if (d.action === 'create' && !d.code) return true;
                  if (d.action === 'link' && !d.code) return true;
                  return false;
                }) || unmatchedTypes.some((u) => !typeDecisions[u.value]?.code)}
                onClick={() => run.mutate()}
              >
                {run.isPending ? 'Importing…' : 'Run Import →'}
              </button>
            </div>
          </div>
        ) : null}
        {step === 'review' && result ? (
          <div id="imp-step-3">
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Import Results</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1, padding: 10, background: 'rgba(34,197,94,.1)', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#22C55E' }}>{result.created ?? 0}</div>
                  <div style={{ fontSize: 10, color: '#22C55E' }}>Created</div>
                </div>
                <div style={{ flex: 1, padding: 10, background: 'rgba(59,130,246,.1)', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#3B82F6' }}>{result.updated ?? 0}</div>
                  <div style={{ fontSize: 10, color: '#3B82F6' }}>Updated</div>
                </div>
                <div style={{ flex: 1, padding: 10, background: 'rgba(245,158,11,.1)', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#F59E0B' }}>{result.skipped ?? 0}</div>
                  <div style={{ fontSize: 10, color: '#F59E0B' }}>Skipped</div>
                </div>
              </div>
              {result.errors && result.errors.length ? (
                <div style={{ fontSize: 11, color: 'var(--danger)' }}>{result.errors.length} error(s) — check individual rows.</div>
              ) : null}
              {result.warnings && result.warnings.length ? (
                <div style={{ marginTop: 10, padding: 10, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', marginBottom: 6 }}>⚠ {result.warnings.length} warning{result.warnings.length === 1 ? '' : 's'}</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10, color: 'var(--muted)', maxHeight: 120, overflowY: 'auto' }}>
                    {result.warnings.slice(0, 50).map((w, i) => <li key={i}>{w}</li>)}
                    {result.warnings.length > 50 ? <li style={{ fontStyle: 'italic' }}>…and {result.warnings.length - 50} more</li> : null}
                  </ul>
                </div>
              ) : null}
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
